import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

const IS_DEV = process.env.NODE_ENV === 'development';

function formatMs(ms) {
  const n = Number(ms) || 0;
  if (n < 1000) return `${Math.round(n)}ms`;
  return `${(n / 1000).toFixed(n >= 10000 ? 1 : 2)}s`;
}

function formatTokens(n) {
  const v = Math.max(0, Math.round(Number(n) || 0));
  return v.toLocaleString();
}

function formatCost(usd) {
  const n = Number(usd) || 0;
  if (n <= 0) return '$0';
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
}

/**
 * Dev-only panel: tokens, estimated Gemini cost, and per-step timings for an AI job.
 */
const BoardAiJobDebug = ({ metrics = null, className = '' }) => {
  const parts = useMemo(
    () => (Array.isArray(metrics?.parts) ? metrics.parts : []),
    [metrics]
  );

  if (!IS_DEV || !metrics) return null;

  const prompt = Number(metrics.promptTokenCount) || 0;
  const output = Number(metrics.candidatesTokenCount) || 0;
  const thinking = Number(metrics.thinkingTokenCount) || 0;
  const total = Number(metrics.totalTokenCount) || (prompt + output + thinking);
  const cached = Number(metrics.cachedContentTokenCount) || 0;
  const budgetRaw = metrics.thinkingBudget;
  const budgetNum = Number(budgetRaw);
  const hasFiniteBudget = Number.isFinite(budgetNum) && budgetNum > 0;
  const thinkLabel = hasFiniteBudget
    ? `${formatTokens(thinking)} / ${formatTokens(budgetNum)} (${Math.min(100, Math.round((thinking / budgetNum) * 100))}%)`
    : budgetNum === 0
      ? `${formatTokens(thinking)} (off)`
      : budgetNum === -1
        ? `${formatTokens(thinking)} (dynamic)`
        : formatTokens(thinking);

  return (
    <Box
      className={`board-ai-job-debug ${className}`.trim()}
      component="details"
      open
    >
      <Box
        component="summary"
        className="board-ai-job-debug-summary"
      >
        Cost {formatCost(metrics.estimatedCostUsd)}
        {' · '}
        {metrics.job || 'job'}
        {metrics.case ? ` · ${metrics.case}` : ''}
        {' · '}
        {formatMs(metrics.totalMs)}
        {metrics.cached ? ' · cached' : ''}
        {hasFiniteBudget ? ` · think ${Math.min(100, Math.round((thinking / budgetNum) * 100))}%` : budgetNum === -1 ? ' · think dynamic' : ''}
      </Box>
      <Box className="board-ai-job-debug-body">
        <Typography component="div" variant="caption" className="board-ai-job-debug-line">
          <span>Model</span>
          <span>{metrics.model || '—'}</span>
        </Typography>
        <Typography component="div" variant="caption" className="board-ai-job-debug-line">
          <span>Tokens</span>
          <span>
            in {formatTokens(prompt)}
            {cached > 0 ? ` (cached ${formatTokens(cached)})` : ''}
            {' · out '}
            {formatTokens(output)}
            {' · think '}
            {thinkLabel}
            {' · Σ '}
            {formatTokens(total)}
          </span>
        </Typography>
        <Typography component="div" variant="caption" className="board-ai-job-debug-line">
          <span>Est. cost</span>
          <span>{formatCost(metrics.estimatedCostUsd)}</span>
        </Typography>
        {parts.length ? (
          <Box className="board-ai-job-debug-parts">
            {parts.map((part, idx) => (
              <Typography
                // eslint-disable-next-line react/no-array-index-key
                key={`${part.key || 'part'}-${idx}`}
                component="div"
                variant="caption"
                className="board-ai-job-debug-part"
              >
                <span className="board-ai-job-debug-part-label">
                  {part.label || part.key || 'Step'}
                </span>
                <span className="board-ai-job-debug-part-ms">{formatMs(part.ms)}</span>
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default BoardAiJobDebug;
